import { useEffect, useState } from 'react';
import { Button } from '@mantine/core';
import { IconDeviceMobile, IconDownload, IconShare2, IconX } from '@tabler/icons-react';
import { useCopy } from '@/lib/settings';

const KEY = 'rides.installHint.dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}
function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua) && /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
}

/** "Add to Home Screen" (iOS Safari) or the Chrome install prompt (Android). Dismissible, remembered. Off when !settings.installHintEnabled. */
export function InstallHint() {
  const { t, settings } = useCopy();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(KEY) === '1'; } catch { return false; }
  });
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => { e.preventDefault(); setPrompt(e as BeforeInstallPromptEvent); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!settings.installHintEnabled || dismissed || isStandalone()) return null;
  const ios = isIosSafari();
  if (!ios && !prompt) return null;

  const dismiss = () => { setDismissed(true); try { localStorage.setItem(KEY, '1'); } catch { /* ignore */ } };

  return (
    <section className="rides-card" style={{ padding: '14px 8px 14px 16px' }} aria-labelledby="rides-install-title">
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ color: 'var(--rides-primary)', display: 'inline-flex', marginTop: 2 }} aria-hidden="true"><IconDeviceMobile size={24} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 id="rides-install-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>{t('shell.install_title')}</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--rides-muted)', fontSize: 15, lineHeight: 1.45 }}>
            {ios ? (
              <>
                <IconShare2 size={16} style={{ verticalAlign: '-2px', marginRight: 4 }} aria-hidden="true" />
                {t('shell.install_ios')}
              </>
            ) : t('shell.install_android')}
          </p>
          {!ios && prompt && (
            <Button
              mt={10}
              variant="light"
              leftSection={<IconDownload size={18} />}
              onClick={async () => { await prompt.prompt(); const c = await prompt.userChoice; if (c.outcome === 'accepted') dismiss(); }}
            >
              Install
            </Button>
          )}
        </div>
        <button type="button" className="rides-iconbtn" onClick={dismiss} aria-label="Hide this tip">
          <IconX size={20} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
